/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {FunctionComponent} from 'react';
import {SensorType} from '@ion/core/src/types/entities';
import {dlsColor} from '../theme/theme';
import {Color, IconProps, IconSize} from '../types/types';
import {DropletIcon} from './DropletIcon';
import {LightIcon} from './LightIcon';
import {LightningBoltIcon} from './LightningBoltIcon';
import {ThermostatIcon} from './ThermostatIcon';
import {TiltArrowIcon} from './TiltArrowIcon';
import {UpArrowIcon} from './UpArrowIcon';

export interface SensorTypeIconProps {
  sensorType: string;
  color?: Color;
  size?: IconSize | number;
}

/**
 * Provides a the mappings between the type prop (Sensor.type) and the Sensor's
 * respective icon.
 */
const sensorTypeIconMap = {
  [SensorType.Light]       : LightIcon,
  [SensorType.Shock]       : LightningBoltIcon,
  [SensorType.Tilt]        : UpArrowIcon,
  [SensorType.Tilted]      : TiltArrowIcon,
  [SensorType.Temperature] : ThermostatIcon,
  [SensorType.Humidity]    : DropletIcon
};

/**
 * @SensorTypeIcon
 *
 * The main purpose of the SensorTypeIcon is to provide a map from the sensor
 * type and map it to the appropriate svg Sensor Icon component.
 *
 * ```typescript
 *
 *    import {SensorTypeIcon} from '@ion/dls/src/icons/SensorTypeIcon';
 *
 *     <SensorTypeIcon sensorType={sensorType.temperature} />
 *
 * ```
 */
export const SensorTypeIcon = ({
  sensorType,
  color = dlsColor('caBlue'),
  size = IconSize.large
}: SensorTypeIconProps) => {
  const SensorIcon: FunctionComponent<IconProps> = sensorTypeIconMap[sensorType] || ThermostatIcon;
  if (sensorType) {
    return (
      <SensorIcon color={color} size={size} />
    );
  }
  return null;
};
